import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

const API_BASE_URL = getApiOrigin();
const TRAINING_VIDEOS_PATH = "/api/v1/trainings/videos";

export interface TrainingVideo {
  id: number;
  name: string;
  category: string | null;
  description: string | null;
  storage_link: string | null;
  is_active: boolean;
  is_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetTrainingVideosParams {
  category?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}

interface TrainingVideosApiResponse {
  success?: boolean;
  message?: string;
  result?: {
    data?: TrainingVideo[];
    total?: number;
    page?: number;
    page_size?: number;
  };
  data?: TrainingVideo[];
  total?: number;
  page?: number;
  page_size?: number;
}

export interface TrainingVideosListResult {
  items: TrainingVideo[];
  total: number;
  page: number;
  pageSize: number;
}

export function resolveTrainingVideoUrls(value?: string | null): string[] {
  const raw = (value || "").trim();
  if (!raw) return [];
  if (/^https?:\/\//i.test(raw)) return [raw];
  if (raw.startsWith("//")) return [`https:${raw}`];

  const baseUrl = ensureBaseUrl();
  const base = new URL(baseUrl);

  // Server-stored video links may arrive as relative paths like:
  // /app/storage/...
  // app/storage/...
  // c/app/storage/...
  // C:\app\storage\...
  // Treat all non-http values as paths on the API host, not as hostnames.
  const normalizedPath = raw
    .replace(/\\/g, "/")
    .replace(/^[A-Za-z]:/, "")
    .replace(/^\/+/, "");

  const pathVariants = new Set<string>();

  if (normalizedPath) {
    pathVariants.add(normalizedPath);
  }

  // Some stored values may contain a drive-like prefix without ":" such as "c/app/storage/...".
  const withoutDriveLikePrefix = normalizedPath.replace(/^[A-Za-z]\//, "");
  if (withoutDriveLikePrefix) {
    pathVariants.add(withoutDriveLikePrefix);
  }

  // Some backends expose files at /storage/... while the stored path includes /app/storage/...
  for (const variant of Array.from(pathVariants)) {
    if (variant.startsWith("app/storage/")) {
      pathVariants.add(variant.replace(/^app\//, ""));
    }
  }

  return Array.from(pathVariants).map((variant) =>
    new URL(`/${variant.replace(/^\/+/, "")}`, base.origin).toString(),
  );
}

export function resolveTrainingVideoUrl(value?: string | null): string {
  return resolveTrainingVideoUrls(value)[0] || "";
}

function ensureBaseUrl(): string {
  if (!API_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }

  return API_BASE_URL;
}

export async function getTrainingVideos(
  params: GetTrainingVideosParams = {},
): Promise<TrainingVideosListResult> {
  const baseUrl = ensureBaseUrl();
  const url = new URL(`${baseUrl}${TRAINING_VIDEOS_PATH}`);

  if (params.category) {
    url.searchParams.set("category", params.category);
  }
  if (typeof params.is_active === "boolean") {
    url.searchParams.set("is_active", String(params.is_active));
  }
  if (typeof params.page === "number") {
    url.searchParams.set("page", String(params.page));
  }
  if (typeof params.page_size === "number") {
    url.searchParams.set("page_size", String(params.page_size));
  }

  const response = await authenticatedFetch(url.toString(), {
    method: "GET",
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as TrainingVideosApiResponse) : null;

  if (!response.ok) {
    throw new Error(
      json?.message || `Failed to fetch training videos (${response.status})`,
    );
  }

  const resultData = json?.result?.data;
  const rootData = json?.data;
  const items = Array.isArray(resultData)
    ? resultData
    : Array.isArray(rootData)
      ? rootData
      : [];

  const total =
    typeof json?.result?.total === "number"
      ? json.result.total
      : typeof json?.total === "number"
        ? json.total
        : items.length;

  const page =
    typeof json?.result?.page === "number"
      ? json.result.page
      : typeof json?.page === "number"
        ? json.page
        : params.page || 1;

  const pageSize =
    typeof json?.result?.page_size === "number"
      ? json.result.page_size
      : typeof json?.page_size === "number"
        ? json.page_size
        : params.page_size || items.length || 1;

  return {
    items,
    total,
    page,
    pageSize,
  };
}

export async function getTrainingVideoPlaybackUrl(
  storageLink?: string | null,
): Promise<string> {
  const candidateUrls = resolveTrainingVideoUrls(storageLink);

  if (candidateUrls.length === 0) {
    throw new Error("Training video link is unavailable");
  }

  let lastStatus: number | null = null;

  for (const resolvedUrl of candidateUrls) {
    const response = await authenticatedFetch(resolvedUrl, {
      method: "GET",
    });

    if (!response.ok) {
      lastStatus = response.status;
      continue;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  throw new Error(
    `Failed to load training video${lastStatus ? ` (${lastStatus})` : ""}`,
  );
}
